'use client'

import {
  needsPickupPointOfSale,
  toPointOfSaleSelectionPage,
} from '@/feature/account/services/account.routing'
import { AUTH_COOKIE_SESSION_EXPIRED_KEY } from '@/feature/auth/services/auth.constant'
import { PICKUP_POINT_OF_SALE_ID_COOKIE } from '@/feature/point-of-sale/services/pointOfSale.constants'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useReactiveGetCookie } from 'cookies-next'
import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

import {
  clearPosSelectedLocally,
  getHasSelectedPosInCurrentSession,
  markPosAsSelectedLocally,
} from '@/feature/point-of-sale/services/pointOfSale.session.client'

type PointOfSaleGuardWrapperProps = {
  isAuthenticated: boolean
  hasFavoritePointOfSale: boolean
  children: React.ReactNode
}

export function PointOfSaleGuardWrapper({
  isAuthenticated,
  hasFavoritePointOfSale,
  children,
}: PointOfSaleGuardWrapperProps) {
  const pathname = usePathname()
  const { replaceWithProgressBar } = useRouter()
  const isHydrated = useNextIsClientHydrated()
  const getCookie = useReactiveGetCookie()
  const pickupPointOfSaleId = getCookie(PICKUP_POINT_OF_SALE_ID_COOKIE)

  // Sync server prop with local state synchronously during render if the cookie is still present
  if (hasFavoritePointOfSale && pickupPointOfSaleId) {
    markPosAsSelectedLocally()
  }

  // Sync client session state with the reactive cookie
  useEffect(() => {
    if (isHydrated) {
      if (pickupPointOfSaleId) {
        markPosAsSelectedLocally()
      } else {
        clearPosSelectedLocally()
      }
    }
  }, [isHydrated, pickupPointOfSaleId])

  const needsPickupPos = needsPickupPointOfSale(pathname)

  // During hydration, rely on server props to prevent SSR/client mismatch.
  // After hydration, rely on reactive client session state.
  const hasSelectedPos = isHydrated ? getHasSelectedPosInCurrentSession() : hasFavoritePointOfSale
  const isUserAuthenticated = isHydrated
    ? getCookie(AUTH_COOKIE_SESSION_EXPIRED_KEY) === 'true'
    : isAuthenticated

  const shouldRedirect = isUserAuthenticated && !hasSelectedPos && needsPickupPos

  useEffect(() => {
    if (isHydrated && shouldRedirect) {
      replaceWithProgressBar(toPointOfSaleSelectionPage)
    }
  }, [isHydrated, shouldRedirect, replaceWithProgressBar])

  if (shouldRedirect) {
    // Prevent flickering of unauthorized page content
    return null
  }

  return <>{children}</>
}
